/* eslint-disable indent */
import Cookies from 'js-cookie';
import { AxiosResponse, AxiosRequestConfig } from 'axios';
import { CLIENT_TYPE } from '@cap/utils';
import {
  isBrowser,
  getWindow,
  getGlobalThis,
  getOrigin,
  getHref,
  getCookie,
  getUserAgent,
} from '@cap/ssr-common-utils';

import { getFrom, isLocalEnv, isTestEnv } from '../../runtimeEnv';
import { createAxios } from '../base';
import { getLxCUUID } from '../../lx/helpers';
import { isSameOriginURL, csrfSafeMethod, getCommonParams, isAbsoluteURL } from '../../urlHelper';
import { logger } from '../../logger';
import { getDeviceType } from '../../uaHelper';
import { ACCOUNT_TYPE, REACT_APP_ENV_MAP, TerminalType, SSR_API_HOST } from '../../../constants';

// 记录API请求处理时间
function apiTime(response: AxiosResponse) {
  const { config } = response;
  const responseTime = Date.now();

  // @ts-ignore
  logger.log(`apitime: ${config.url} : ${responseTime - config.requestTime}`);
}

interface InnerHeaderProps {
  /**
   * 运行时环境
   */
  env?: REACT_APP_ENV_MAP;
  /**
   * 是否B端账号流程
   */
  accountType?: ACCOUNT_TYPE;
  /**
   * 终端类型
   */
  terminalType?: TerminalType;
  /**
   * 业务渠道标识
   */
  source?: string;
  /**
   *  设置基础URL，用于发送请求时的URL拼接
   */
  baseURL?: string;
  /**
   * 访问的url地址
   */
  url?: string;
  /**
   * 请求方法，默认： POST
   */
  method?: string;
  /**
   * 当前访问的href，ssr下传递
   */
  href?: string;
  /**
   * ua信息，ssr下传递
   */
  userAgent?: string;
  /**
   * 租户
   */
  tenant?: string;
  tenantId?: string;
  /**
   * 泳道标识，访问泳道服务才传递
   */
  swimlane?: string;
  /**
   * ssr 需要传递用户访问的uuid
   */
  uuid?: string;
}

/**
 * 创建ajax的通用headers
 * @param param0
 * @returns
 */
export const createHeaders = ({
  baseURL,
  url,
  method,
  href,
  userAgent,
  tenant,
  tenantId,
  accountType,
  terminalType,
  env,
  swimlane,
  uuid,
}: InnerHeaderProps) => {
  const headers: Record<string, string> = {};
  const that = getGlobalThis();
  const window = getWindow();
  // 同域请求添加 x-requested-with header
  if (isSameOriginURL(url as string, baseURL)) {
    const {
      env: rEnv = REACT_APP_ENV_MAP.production,
      accountType: accType = ACCOUNT_TYPE.PASSPORT,
      swimlane: rSwimlane,
      terminalType: rTermType = TerminalType.Mobile,
    } = that.runtime_env ?? {};
    const currEnv = env || rEnv || REACT_APP_ENV_MAP.production;
    const { mtdxSource = 'mtdx' } = getCommonParams(href);
    headers['X-Tenant'] = (tenant || '') as string;
    headers['X-Tenant-Id'] = (tenantId || '') as string;
    headers['X-Source'] = (mtdxSource || 'mtdx') as string;
    headers['X-Device-Id'] = isBrowser ? getLxCUUID() : (uuid as string);
    headers['X-Device-Type'] = getDeviceType(userAgent);
    headers['X-UUID'] =
      Cookies.get('uuid') ||
      Cookies.get('iuuid') ||
      Cookies.get('_lxsdk_cuid') ||
      Cookies.get('_lxsdk') ||
      '';

    headers['X-Client-Type'] = (href || window.location.href).includes('__msgMock=true')
      ? CLIENT_TYPE.meituan
      : getFrom(userAgent); // 增加客服端类型参数
    // headers['X-Client-Name'] = clientName as string;

    const currAccType = accountType || accType;
    headers['X-Account-Type'] = currAccType.toString(); // 账号类型 c: 2  ep: 3
    headers['X-Account-From'] = currAccType.toString();
    // 测试环境增加泳道变量
    const currSwimlane = swimlane || rSwimlane;
    if ((isLocalEnv(currEnv) || isTestEnv(currEnv)) && currSwimlane) {
      headers['x-shepherd-swimlane'] = currSwimlane;
    }
    // 添加csrf必要的header
    if (isBrowser && !csrfSafeMethod(method as string)) {
      headers['x-csrf-token'] = Cookies.get('csrfToken') as string;
    }
    headers['x-requested-with'] = 'XMLHttpRequest';
    headers['X-Terminal-Type'] = (terminalType ?? rTermType).toString();
  }

  return headers;
};

/**
 * 创建服务端请求的headers
 * @param ctx
 * @returns
 */
export const createSSRHeaders = () => {
  if (isBrowser) {
    return {};
  }
  const href = getHref();
  const cookie = getCookie() || '';
  const uakey = 'user-agent';
  const userAgent = getUserAgent();
  const uuid = getLxCUUID();
  let headers = createHeaders({ href, userAgent, uuid });
  headers[uakey] = userAgent;
  headers.cookie = cookie;
  // 过滤掉空值的key
  headers = Object.keys(headers)
    .filter((key) => Boolean(headers[key]))
    .reduce((pre, key) => {
      pre[key] = headers[key];
      return pre;
    }, {} as Record<string, string>);
  return headers;
};

/**
 * 接口响应200拦截器, 主要格式化接口返回数据，统一处理错误信息
 * @param res
 * @returns
 */
export const successfulInterceptor = (res: any): Promise<string> => {
  const { message, status = {} } = res;

  if (status.code === 0) {
    return res;
  }
  // 当访问第三方接口时，直接返回请求结果
  if (!status || !status.code) {
    return res;
  }

  logger.warn({
    name: 'code非0错误',
    // @ts-ignore
    msg: `code非0错误，详情：${JSON.stringify(res)}；错误码：${status.code}`,
  });

  return Promise.reject(new Error(message || status.message));
};

const defaultBaseUrl = '/spapi/v2/client';

export const getBaseUrl = (appEnv: REACT_APP_ENV_MAP) => {
  if (isBrowser) {
    return defaultBaseUrl;
  }
  let serverHost = SSR_API_HOST[appEnv];
  if ([REACT_APP_ENV_MAP.test, REACT_APP_ENV_MAP.staging].includes(appEnv)) {
    serverHost = getOrigin() || serverHost;
  }
  return `${serverHost}${defaultBaseUrl}`;
};

export const createInstance = () => {
  return createAxios({
    baseURL: defaultBaseUrl,
    requestInterceptor(config: AxiosRequestConfig) {
      // @ts-ignore
      config.requestTime = Date.now();
      config.params = {
        ...(config.params || {}),
      };
      const { tenant, tenantId } = config.params;
      const { url, baseURL, method } = config;
      const headers = isBrowser ? createHeaders({ baseURL, url, method, tenant, tenantId }) : {};
      config.headers = {
        ...headers,
        ...(config.headers || {}),
      };
      return config;
    },

    responseInterceptor(response: AxiosResponse) {
      const httpStatusCode = parseInt(`${response.status}`, 10);
      if (!isBrowser) {
        console.log('response.headers', {
          traceid: response.headers?.['m-traceid'],
          apiPath: response.headers?.['m-spanname'],
        });
      }
      // @ts-ignore
      if (response.config?.config?.outerUrl) {
        if (httpStatusCode === 200) {
          return response.data;
        }
        return Promise.reject(new Error('拒绝访问'));
      }

      const {
        data: reqData,
        headers,
        method,
        responseType,
        timeout,
        withCredentials,
        baseURL,
      } = response.config;
      let url = '';
      if (isBrowser) {
        url = response.request.responseURL;
      } else {
        url = response.request.res?.responseUrl || '';
      }
      // 非绝对地址，拼接上baseURL
      if (!isAbsoluteURL(url)) {
        url = url.includes(baseURL as string) ? url : `${baseURL}${url}`;
      }
      const { headers: requestHeaders } = response;
      const catParams = {
        url,
        data: reqData,
        headers,
        method,
        responseType,
        timeout,
        withCredentials,
      };

      const ajaxInfo = {
        request: catParams,
        response: response.data,
      };
      apiTime(response);
      if (httpStatusCode === 200) {
        const res = response.data || {};
        return successfulInterceptor(res);
      }
      logger.warn({
        name: '接口非200错误',
        // @ts-ignore
        msg: `接口非200错误，详情：${JSON.stringify(ajaxInfo)}；错误码：${response.status}`,
      });
      if (requestHeaders['x-forbid-reason'] || requestHeaders['X-Forbid-Reason']) {
        return Promise.reject(new Error('Forbid-Reason'));
      }
      return Promise.reject(new Error('请求异常'));
    },
  });
};

const instance = createInstance();

export default instance;
